import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import SubscriptionLock from '@/components/SubscriptionLock';
import { hasFullAccess } from '@/lib/subscription';
import { extractErrorMessage } from '@/lib/errors';
import { Warehouse, AlertTriangle, Loader2, ListChecks, PenLine, FileSpreadsheet } from 'lucide-react';

// Same unit vocabulary the manufacturer catalog uses, so a distributor
// picking a stocking measure sees an identical list. Labels come from
// the shared `products.units.*` i18n namespace.
const UNIT_KEYS = ['piece', 'kg', 'g', 'litre', 'ml', 'bag', 'pack', 'bottle', 'carton'];

// The three ways to add products (beta feedback): each opens its screen
// on /my-products/add, which comes back here once something is added.
const ADD_WAYS = [
  { key: 'list', icon: ListChecks },
  { key: 'manual', icon: PenLine },
  { key: 'upload', icon: FileSpreadsheet },
];

/**
 * My stock (distributors and shops): how to add products, then one row per
 * product — product, quantity (typed freely, decimals allowed) and the unit
 * it's counted in. A change is saved as soon as the field is left.
 */
export default function InventoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [inventory, setInventory] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const loadInventory = async () => {
    try {
      const [invRes, lowRes] = await Promise.all([
        inventoryAPI.get(user.id),
        inventoryAPI.getLowStock(user.id),
      ]);
      setInventory(invRes.data || []);
      setLowStock(lowRes.data || []);
    } catch (error) {
      console.error('Failed to load inventory', error);
    } finally {
      setLoading(false);
    }
  };

  const save = async (item, fields) => {
    const quantity = fields.quantity ?? item.quantity;
    const unit = fields.unit ?? item.unit;
    const n = Number(String(quantity).replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) {
      toast({ title: t('inventory.toast.qtyPositive'), variant: 'destructive' });
      return;
    }
    if (n === Number(item.quantity) && unit === item.unit) return;
    setSavingId(item.id);
    try {
      const res = await inventoryAPI.update(item.id, { quantity: n, unit: unit || null });
      setInventory((rows) => rows.map((r) => (r.id === item.id
        ? { ...r, quantity: Number(res.data.quantity ?? n), unit: res.data.unit ?? unit } : r)));
      toast({ title: t('inventory.toast.saved') });
    } catch (error) {
      toast({ title: extractErrorMessage(error, t('inventory.toast.failed')), variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-farm-primary" /></div>;
  }

  const hasSubscription = hasFullAccess(user);
  if (!hasSubscription) {
    return (
      <SubscriptionLock
        bodyKey="subscriptionLock.body.inventory"
        userType="distributor"
        benefitKeys={['invMgmt', 'shopNetwork', 'orderTracking', 'salesAnalytics']}
      />
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 animate-fadeIn">
      <h1 className="font-heading text-2xl md:text-3xl font-bold">{t('inventory.title')}</h1>
      <p className="text-muted-foreground mb-4">{t('inventory.subtitle')}</p>

      {/* How to add products: pick from the list, type one in, or a spreadsheet. */}
      <p className="font-semibold mb-2">{t('inventory.addHow')}</p>
      <div className="grid grid-cols-3 gap-2 mb-6" data-testid="stock-add-ways">
        {ADD_WAYS.map(({ key, icon: Icon }) => (
          <button key={key} type="button" onClick={() => navigate(`/my-products/add?tab=${key}`)}
                  className="rounded-2xl bg-farm-primary text-white px-2 py-3 min-h-[6rem] flex flex-col items-center justify-center gap-1.5 text-center shadow-sm active:scale-[0.98]"
                  data-testid={`stock-add-${key}`}>
            <Icon className="w-7 h-7" />
            <span className="text-sm font-semibold leading-tight">{t(`inventory.addWays.${key}`)}</span>
          </button>
        ))}
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <Card className="mb-6 border-farm-warning bg-farm-warning/5">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-farm-warning">
              <AlertTriangle className="w-5 h-5" />
              {t('inventory.lowStock.title', { count: lowStock.length })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {lowStock.map((item) => (
                <span key={item.id} className="px-3 py-1 bg-farm-warning/10 text-farm-warning rounded-full text-sm">
                  {t('inventory.lowStock.chip', { name: item.product_name, qty: item.quantity })}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {inventory.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Warehouse className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">{t('inventory.empty.title')}</h3>
            <p className="text-muted-foreground">{t('inventory.empty.chooseAbove')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="stock-list">
          <div className="grid grid-cols-[1fr_84px_112px] gap-2 px-3 py-2 bg-gray-50 text-xs font-semibold text-muted-foreground">
            <span>{t('inventory.cols.product')}</span>
            <span>{t('inventory.cols.quantity')}</span>
            <span>{t('inventory.cols.unit')}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {inventory.map((item) => (
              <StockRow key={item.id} item={item} saving={savingId === item.id} onSave={save} t={t} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StockRow({ item, saving, onSave, t }) {
  const [qty, setQty] = useState(String(item.quantity ?? ''));
  useEffect(() => { setQty(String(item.quantity ?? '')); }, [item.quantity]);
  const low = item.reorder_level != null && Number(item.quantity) <= Number(item.reorder_level);
  const unitKeys = item.unit && !UNIT_KEYS.includes(item.unit) ? [...UNIT_KEYS, item.unit] : UNIT_KEYS;
  return (
    <li className="grid grid-cols-[1fr_84px_112px] gap-2 items-center px-3 py-2.5" data-testid="stock-row">
      <div className="min-w-0">
        <p className="font-semibold break-words leading-snug flex items-center gap-1">
          {low && <AlertTriangle className="w-4 h-4 text-farm-warning shrink-0" />}
          {item.product_name}
        </p>
        {item.nafdac_number && (
          <p className="text-xs text-muted-foreground">
            NAFDAC {item.nafdac_number}
            {item.master_status === 'pending' && (
              <span className="ml-1 rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 font-semibold">{t('myProducts.pending')}</span>
            )}
            {item.master_status === 'rejected' && (
              <span className="ml-1 rounded-full bg-red-100 text-red-700 px-1.5 py-0.5 font-semibold">{t('myProducts.rejected')}</span>
            )}
          </p>
        )}
      </div>
      <div className="relative">
        <input
          inputMode="decimal"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          onBlur={() => onSave(item, { quantity: qty })}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-full h-11 rounded-lg border border-gray-300 px-2 text-base"
          aria-label={t('inventory.cols.quantityOf', { name: item.product_name })}
          data-testid="stock-qty"
        />
        {saving && <Loader2 className="w-4 h-4 animate-spin absolute right-1.5 top-3.5 text-farm-primary" />}
      </div>
      <Select value={item.unit || ''} onValueChange={(v) => onSave(item, { quantity: qty, unit: v })}>
        <SelectTrigger className="h-11 bg-white" data-testid="stock-unit">
          <SelectValue placeholder={t('inventory.form.measurePlaceholder')} />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {unitKeys.map((key) => (
            <SelectItem key={key} value={key}>{t(`products.units.${key}`, key)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </li>
  );
}
